import request from '@/utils/request'
import {
  MedicalBusinessRegisterRequest,
  MedicalBusinessRegisterResponse,
  MedicalBusinessRegisterWithConsultationRequest,
  MedicalBusinessRegisterWithConsultationResponse
} from '@/interface/api'
import { AlertModule } from '@/store/modules/alert'

const prefix = 'api/v0/customer/web-first-reservation'

export const createTreatment = (
  medicalBusinessId: string,
  temporaryReservationId: string,
  medicalTreatment: MedicalBusinessRegisterRequest
): Promise<MedicalBusinessRegisterResponse> =>
  request({
    url: `${prefix}/${medicalBusinessId}/${temporaryReservationId}/`,
    method: 'post',
    data: medicalTreatment
  })
    .then(res => {
      const response: MedicalBusinessRegisterResponse = {
        result: true,
        message: '',
        res: res.data.data.attributes
      }
      return response
    })
    .catch(e => {
      if (
        e.response.data.data.attributes.message ===
        'already_same_phone_number_is_registered'
      ) {
        AlertModule.SetErrorAlert({
          errorTime: new Date(),
          message: '既に登録されている携帯番号です'
        })
        const response: MedicalBusinessRegisterResponse = {
          result: false,
          message: 'already_same_phone_number_is_registered',
          res: null
        }
        return response
      } else {
        AlertModule.SetErrorAlert({
          errorTime: new Date(),
          message: '診察登録に失敗しました。'
        })
        const response: MedicalBusinessRegisterResponse = {
          result: false,
          message: 'error',
          res: null
        }
        return response
      }
    })

export const createTimeframeTreatment = (
  medicalBusinessId: string,
  medicalTreatment: MedicalBusinessRegisterRequest
): Promise<MedicalBusinessRegisterResponse> =>
  request({
    url: `${prefix}/${medicalBusinessId}/`,
    method: 'post',
    data: medicalTreatment
  })
    .then(res => {
      const response: MedicalBusinessRegisterResponse = {
        result: true,
        message: '',
        res: res.data.data.attributes
      }
      return response
    })
    .catch(e => {
      if (
        e.response.data.data.attributes.message ===
        'already_same_phone_number_is_registered'
      ) {
        AlertModule.SetErrorAlert({
          errorTime: new Date(),
          message: '既に登録されている携帯番号です'
        })
        const response: MedicalBusinessRegisterResponse = {
          result: false,
          message: 'already_same_phone_number_is_registered',
          res: null
        }
        return response
      } else {
        AlertModule.SetErrorAlert({
          errorTime: new Date(),
          message: '診察登録に失敗しました。'
        })
        const response: MedicalBusinessRegisterResponse = {
          result: false,
          message: 'error',
          res: null
        }
        return response
      }
    })

// 統合API: 時間指定予約 + 事前問診回答
export const createTreatmentWithConsultation = (
  medicalBusinessId: string,
  temporaryReservationId: string,
  requestData: MedicalBusinessRegisterWithConsultationRequest
): Promise<MedicalBusinessRegisterWithConsultationResponse> => {
  const formData = new FormData()

  // customer_json
  formData.append('customer_json', JSON.stringify(requestData.customer))

  // treatment_json
  formData.append('treatment_json', JSON.stringify(requestData.treatment))

  // consultation_json
  formData.append('consultation_json', JSON.stringify(requestData.consultation))

  // inflow_id
  formData.append('inflow_id', requestData.inflow_id)

  // credit_card_json (オプショナル)
  if (requestData.credit_card) {
    formData.append('credit_card_json', JSON.stringify(requestData.credit_card))
  }

  // insurance_card_front_image, insurance_card_back_image (オプショナル)
  if (requestData.insurance?.first_image) {
    formData.append(
      'insurance_card_front_image',
      requestData.insurance.first_image
    )
  }
  if (requestData.insurance?.second_image) {
    formData.append(
      'insurance_card_back_image',
      requestData.insurance.second_image
    )
  }

  // 問診回答のファイル (例: 0-0, 0-1, etc.)
  requestData.uploadFiles.forEach(uploadFile => {
    formData.append(uploadFile.key, uploadFile.file)
  })

  return request({
    url: `${prefix}/consultation-answer/${medicalBusinessId}/${temporaryReservationId}/`,
    method: 'post',
    data: formData
  })
    .then(res => {
      const response: MedicalBusinessRegisterWithConsultationResponse = {
        result: true,
        message: '',
        res: res.data.data.attributes
      }
      return response
    })
    .catch(e => {
      // エラーレスポンスからメッセージを取得
      const errorMessage =
        e.response?.data?.data?.attributes?.message || 'error'

      // エラーメッセージをそのまま返す（呼び出し側で処理）
      const response: MedicalBusinessRegisterWithConsultationResponse = {
        result: false,
        message: errorMessage,
        res: null
      }
      return response
    })
}

// 統合API: 時間帯予約 + 事前問診回答
export const createTimeframeTreatmentWithConsultation = (
  medicalBusinessId: string,
  requestData: MedicalBusinessRegisterWithConsultationRequest
): Promise<MedicalBusinessRegisterWithConsultationResponse> => {
  const formData = new FormData()

  // customer_json
  formData.append('customer_json', JSON.stringify(requestData.customer))

  // treatment_json
  formData.append('treatment_json', JSON.stringify(requestData.treatment))

  // consultation_json
  formData.append('consultation_json', JSON.stringify(requestData.consultation))

  // inflow_id
  formData.append('inflow_id', requestData.inflow_id)

  // credit_card_json (オプショナル)
  if (requestData.credit_card) {
    formData.append('credit_card_json', JSON.stringify(requestData.credit_card))
  }

  // insurance_card_front_image, insurance_card_back_image (オプショナル)
  if (requestData.insurance?.first_image) {
    formData.append(
      'insurance_card_front_image',
      requestData.insurance.first_image
    )
  }
  if (requestData.insurance?.second_image) {
    formData.append(
      'insurance_card_back_image',
      requestData.insurance.second_image
    )
  }

  // 問診回答のファイル (例: 0-0, 0-1, etc.)
  requestData.uploadFiles.forEach(uploadFile => {
    formData.append(uploadFile.key, uploadFile.file)
  })

  return request({
    url: `${prefix}/consultation-answer/${medicalBusinessId}/`,
    method: 'post',
    data: formData
  })
    .then(res => {
      const response: MedicalBusinessRegisterWithConsultationResponse = {
        result: true,
        message: '',
        res: res.data.data.attributes
      }
      return response
    })
    .catch(e => {
      // エラーレスポンスからメッセージを取得
      const errorMessage =
        e.response?.data?.data?.attributes?.message || 'error'

      // エラーメッセージをそのまま返す（呼び出し側で処理）
      const response: MedicalBusinessRegisterWithConsultationResponse = {
        result: false,
        message: errorMessage,
        res: null
      }
      return response
    })
}
